import React from 'react';

export const DOTS = '...' as const;

export function usePaginationRange ({
  totalItems,
  currentPage,
  rowsPerPage,
  siblingCount = 1,
}: {
  totalItems: number;
  currentPage: number;
  rowsPerPage: number;
  siblingCount?: number;
}): { totalPages: number; paginationRange: Array<number | typeof DOTS> } {
  const totalPages = Math.max(
    1,
    Math.ceil((totalItems ?? 0) / Math.max(1, rowsPerPage ?? 1))
  );

  const paginationRange = React.useMemo(() => {
    if (totalPages <= 1) {
      return [1];
    }

    const firstPage = 1;
    const lastPage = totalPages;
    const left = Math.max(currentPage - siblingCount, firstPage);
    const right = Math.min(currentPage + siblingCount, lastPage);
    const showLeftDots = left > firstPage + 1;
    const showRightDots = right < lastPage - 1;
    const pages: Array<number | typeof DOTS> = [firstPage];

    if (showLeftDots) {
      pages.push(DOTS);
    } else {
      for (let i = firstPage + 1; i < left; i++) {
        pages.push(i);
      }
    }

    for (let i = left; i <= right; i++) {
      if (i !== firstPage && i !== lastPage) {
        pages.push(i);
      }
    }

    if (showRightDots) {
      pages.push(DOTS);
    } else {
      for (let i = right + 1; i < lastPage; i++) {
        pages.push(i);
      }
    }

    if (lastPage !== firstPage) {
      pages.push(lastPage);
    }

    return pages;
  }, [totalPages, currentPage, siblingCount]);

  return { totalPages, paginationRange };
}
